import * as angular from 'angular';
import * as _ from 'lodash';

import { IRootScope } from '../../IRootScope';
import { ComponentDefinition, ConnectionPoint, Dictionary, TagDefinition, TagDefinitionDTO, VariableSubType, VariableType } from '../../models';
import { defaultItemPerPage, tagDefaultState, tagSelected } from '../../common';
import { ITagDefinitionMapper, ILoggerService } from '../../services';

class TagsSelectionController implements angular.IComponentController {
    connectionPoints: ConnectionPoint[];
    displayedTags: TagDefinition[];
    enable = { ms: true, tag: true, identifier: true };
    filteredTags: TagDefinition[];
    formController: angular.IFormController;
    hasMore: boolean;
    isLoading: boolean;
    loadingMessage: string;
    pageLength = defaultItemPerPage;
    pageNumber = 1;
    searchFormat = {
        // custom search function should return 'true' only if should be visible
        ms: t => String(t.min === undefined || t.max === undefined || t.stdDevThreshold === undefined),
        tag: f => f.name
    };
    searchString: string;
    selectedTag: TagDefinition;
    tagsDto: TagDefinitionDTO[];
    tags: TagDefinition[];
    variableTypesDictionary: Dictionary<VariableType>;
    variableSubTypesDictionary: Dictionary<VariableSubType>;

    private _garbage: (() => void);

    static $inject = [
        '$scope',
        '$rootScope',
        '$translate',
        '$state',
        'tagDefinitionMapper',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$translate: angular.translate.ITranslateService,
        private _$state: angular.ui.IStateService,
        private _tagDefinitionMapper: ITagDefinitionMapper,
        private _loggerService: ILoggerService,
    ) { }

    $onInit() {
        this.refresh();
    }

    $onDestroy() {
        this.cleanUp();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['tagsDto'] && changesObj['tagsDto'].currentValue) {
            this.refresh();
        }
    }

    private refresh() {
        if (this.tagsDto) {
            this.read();
            this.cleanUp();
            this._garbage = this._$scope.$watch(
                () => this.filteredTags,
                (a, b) => {
                    if (a !== b) {
                        this.filter();
                    }
                }
            );
        }
    }

    private cleanUp() {
        if (this._garbage) {
            this._garbage();
        }
    }

    private read() {
        this.tags = this._tagDefinitionMapper.mapTagsVm(this.tagsDto, this.connectionPoints, this.variableTypesDictionary, this.variableSubTypesDictionary);
    }

    private filter() {
        const n = Math.min(this.filteredTags.length, this.pageNumber * this.pageLength);
        let filtered = _(this.filteredTags);
        filtered = filtered.take(n);
        this.displayedTags = filtered.value();
        this.hasMore = (n < this.filteredTags.length);

        let reselect = this.displayedTags[0];
        if (this.selectedTag) {
            const found = _.find(this.displayedTags, t => t.guid === this.selectedTag.guid);
            if (found) {
                reselect = found;
            }
        }
        this.setClickedRow(reselect, 0);
    }

    getIconTitle(tag: TagDefinition) {
        return this._$translate.instant(
            'Min: _min_, Max: _max_, Std dev tresh: _std_', { 'min': tag.min, 'max': tag.max, 'std': tag.stdDevThreshold }
        );
    }

    more() {
        this.pageNumber++;
        this.filter();
    }

    missAllStats(tag: TagDefinition): boolean {
        return tag.min === undefined && tag.max === undefined && tag.stdDevThreshold === undefined;
    }

    missSomeStats(tag: TagDefinition): boolean {
        return tag.min === undefined || tag.max === undefined || tag.stdDevThreshold === undefined;
    }

    setClickedRow(tag, $index: number): void {
        if (this.formController && this.formController.$dirty) {
            const msg = this._$translate.instant('Save or cancel changes before selecting another tag.');
            this._loggerService.logWarning(msg);
        } else {
            // TODO don't reset array when implement multiselection
            this.selectedTag = tag;
            this._$rootScope.$broadcast(tagSelected, this.selectedTag);
        }
    }

    selectItem(tag: TagDefinition) {
        if (!this._$rootScope.isEditMode) {
            this._$state.go(tagDefaultState, { tagDefinitionGuid: tag.guid, tag: tag });
        }
    }
}

export const wtTagsSelection: ComponentDefinition = {
    componentId: 'wtTagsSelection',
    controller: TagsSelectionController,
    template: require('./tagsSelection.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedTag: '=',
        formController: '=',
        searchString: '=?',
        tagsDto: '<',
        variableTypesDictionary: '<',
        variableSubTypesDictionary: '<',
        connectionPoints: '<'
    }
};
